import { Fragment, useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { SubUserActions, SubUserPanel, FirstLoginBadge, locText } from "./SubUserCard";
import BulkMoveBar from "./BulkMoveBar";

const HEADERS = ["Ad Soyad", "E-posta", "Telefon", "Taşeron", "Projeler", "Şube Kısıtı", ""];

// Tüm taşeron kullanıcıları tek tabloda: ara, seç → toplu taşı, taşerona git, düzenle / kopyala / sil
export default function SubUsersAll({ users, caris, onChanged, onCopied, onOpenCari }) {
  const [q, setQ] = useState("");
  const [mode, setMode] = useState({});   // { [userId]: "edit" | "copy" }
  const [sel, setSel] = useState([]);
  const shown = useMemo(() => {
    const s = q.trim().toLocaleLowerCase("tr-TR");
    return users.filter((u) => !s || [u.name, u.email, u.phone, u.cari_name, ...u.projects].some((x) => (x || "").toLocaleLowerCase("tr-TR").includes(s)));
  }, [users, q]);
  const setUserMode = (id) => (m) => setMode((s) => ({ ...s, [id]: m }));
  const allShown = shown.length > 0 && shown.every((u) => sel.includes(u.id));
  const toggleAll = () => setSel(allShown ? sel.filter((id) => !shown.some((u) => u.id === id)) : [...new Set([...sel, ...shown.map((u) => u.id)])]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="sub-users-all">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-3 flex-wrap">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad, e-posta, telefon, taşeron veya proje ile ara…" data-testid="sub-users-search" className="px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white w-96" />
        <span className="text-xs text-slate-500" data-testid="sub-users-count">{shown.length} / {users.length} kullanıcı</span>
        <span className="text-[11px] text-slate-400">Satırları işaretleyip birden çok kullanıcıyı tek seferde başka taşerona taşıyabilirsiniz.</span>
      </div>
      <BulkMoveBar selected={sel} caris={caris} onDone={() => { setSel([]); onChanged?.(); }} onClear={() => setSel([])} />
      <table className="w-full tbl">
        <thead className="bg-slate-50"><tr>
          <th className="px-3 py-2.5 w-8"><input type="checkbox" checked={allShown} onChange={toggleAll} data-testid="sub-users-select-all" title="Görünen tümünü seç" /></th>
          {HEADERS.map((h) => <th key={h} className="px-3 py-2.5 text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">{h}</th>)}
        </tr></thead>
        <tbody>
          {shown.map((u) => (
            <Fragment key={u.id}>
              <tr className={`border-t border-slate-100 ${sel.includes(u.id) ? "bg-orange-50" : mode[u.id] ? "bg-slate-50" : "hover:bg-slate-50"}`} data-testid={`sub-users-row-${u.id}`}>
                <td className="px-3 py-2"><input type="checkbox" checked={sel.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`sub-users-select-${u.id}`} /></td>
                <td className="px-3 py-2 text-xs font-semibold">{u.name}<FirstLoginBadge u={u} /></td>
                <td className="px-3 py-2 text-xs">{u.email}</td>
                <td className="px-3 py-2 text-xs">{u.phone || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  {u.cari_missing || u.cari_deleted
                    ? <span className="inline-flex items-center gap-1 text-red-700 font-semibold" title={u.cari_missing ? "Kullanıcının bağlı olduğu cari yok — Düzenle ile taşeron atayın" : "Bağlı cari silinmiş"}><AlertTriangle className="w-3 h-3" /> {u.cari_name || "Cari atanmamış"}</span>
                    : <button type="button" onClick={() => onOpenCari(u.subcontractor_id)} data-testid={`sub-users-cari-${u.id}`} className="text-orange-700 font-semibold hover:underline text-left" title="Taşeron detayına git">{u.cari_name}</button>}
                </td>
                <td className="px-3 py-2 text-xs">{u.projects.join(", ") || "—"}</td>
                <td className="px-3 py-2 text-[11px] text-slate-600">{locText(u)}</td>
                <td className="px-3 py-2"><SubUserActions u={u} mode={mode[u.id]} setMode={setUserMode(u.id)} onChanged={onChanged} testid={`sub-users-${u.id}`} /></td>
              </tr>
              {mode[u.id] && <tr className="border-t border-slate-100 bg-slate-50"><td colSpan={8} className="px-3 py-2"><SubUserPanel u={u} caris={caris} mode={mode[u.id]} setMode={setUserMode(u.id)} onChanged={onChanged} onCopied={onCopied} testid={`sub-users-${u.id}`} /></td></tr>}
            </Fragment>
          ))}
          {shown.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-sm text-slate-500">Taşeron kullanıcısı yok</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
